import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { CheckCircle2, Plus } from 'lucide-react';
import { Button, cn } from '@clarix/ui';

interface EmptyStateProps {
  title: string;
  description?: string;
  icon?: LucideIcon;
  /** Convenience primary action — renders a Button with a leading Plus icon. */
  actionLabel?: string;
  onAction?: () => void;
  /** Custom primary action node. Takes precedence over actionLabel/onAction. */
  action?: ReactNode;
  /** Secondary action rendered next to the primary. */
  secondaryAction?: ReactNode;
  /**
   * Fill the parent's height and centre vertically within it, for a page-level
   * empty state that replaces a table. Off by default — inline empties (a card, a
   * preview panel) keep their natural height. Needs a flex-column parent with a
   * resolved height, which `PageShell.Body` always is.
   */
  fill?: boolean;
  className?: string;
}

export function EmptyState({
  title,
  description,
  icon: Icon,
  actionLabel,
  onAction,
  action,
  secondaryAction,
  fill = false,
  className,
}: EmptyStateProps) {
  const hasCustomActions = Boolean(action || secondaryAction);

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center px-4 py-12 text-center',
        // `fill` centres a page-level empty state in the remaining height. `flex-1`
        // makes it consume the free space of its flex-column parent (PageShell.Body)
        // and `my-auto` centres the box itself even if flex-grow is contested — the
        // two together survive both a stretched and an unstretched parent.
        fill && 'my-auto min-h-full flex-1',
        className,
      )}
    >
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-indigo-50">
        {Icon ? (
          <Icon className="h-8 w-8 text-indigo-500" strokeWidth={1.5} />
        ) : (
          <CheckCircle2 className="h-8 w-8 text-indigo-500" strokeWidth={1.5} />
        )}
      </div>

      <h3 className="text-foreground mb-1 text-3xl font-medium">{title}</h3>
      {description && (
        <p className="text-muted-foreground mb-4 max-w-sm text-xl">{description}</p>
      )}

      {hasCustomActions ? (
        <div className="flex items-center gap-2">
          {action}
          {secondaryAction}
        </div>
      ) : (
        actionLabel &&
        onAction && (
          <Button onClick={onAction} size="xs" className="gap-2">
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            {actionLabel}
          </Button>
        )
      )}
    </div>
  );
}
